import React from 'react';
import {
    Box,
    Button,
    Container,
    Grid,
    Typography,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    Divider,
    IconButton,
    Tooltip,
    Paper
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import ConferenceNavbar from "../../frontend/Conference/ConferenceNavbar";
import NewVersionAbstractFooter from "../AbstractAuth/NewVersion/NewVersionAnstractFooter";
import { useDeadline } from "../../contexts/DeadlineContext";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentIcon from '@mui/icons-material/Payment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

const ThankYouConferenceParticipant = () => {
    const { abstractHomeSetup } = useDeadline();
    const location = useLocation();
    const participantId = location.pathname.split("/").pop();
    const badgeUrl = location.state?.badge_url;
   
    const primaryColor = abstractHomeSetup.color_code_two || '#05597D';
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // Function to copy text to clipboard
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    // Parse bank details (assuming format is consistent)
    const parseBankDetails = (details) => {
        if (!details) return null;
        
        // This is a simple parser - adjust based on your actual bank details format
        const lines = details.split('\n');
        const result = {
            bankName: lines[0]?.replace('Bank Name:', '').trim() || '',
            accountName: lines[1]?.replace('Account Name:', '').trim() || '',
            accountNumber: lines[2]?.replace('Account Number:', '').trim() || '',
            iban: lines[3]?.replace('IBAN:', '').trim() || '',
            swiftCode: lines[4]?.replace('SWIFT Code:', '').trim() || '',
            additionalInfo: lines.slice(5).join('\n') || ''
        };
        
        return result;
    };

    const bankDetails = parseBankDetails(abstractHomeSetup.c_bank_details);

    return (
        <Box display="flex" flexDirection="column" minHeight="100vh" bgcolor="#f5f7fa">
            <Grid item xs={12} sm={10} md={8} lg={6}>
                <ConferenceNavbar />
            </Grid>

            {/* Top-right Back Button */}
            <Box display="flex" justifyContent="flex-end" alignItems="center" p={2}>
                <Button
                    component={Link}
                    to="/conference/particpant-verification"
                    variant="contained"
                    size="small"
                    sx={{
                        backgroundColor: primaryColor,
                        '&:hover': {
                            backgroundColor: primaryColor,
                            opacity: 0.9,
                        },
                    }}
                >
                    Back to Verification
                </Button>
            </Box>

            {/* Main Content */}
            <Container maxWidth="md" sx={{ py: 4, flexGrow: 1 }}>
                <Paper elevation={3} sx={{ p: 4, borderRadius: 3, mb: 4 }}>
                    <Typography 
                        variant={isSmallScreen ? 'h5' : 'h4'} 
                        gutterBottom 
                        align="center"
                        sx={{ 
                            color: primaryColor,
                            fontWeight: 'bold',
                            mb: 3
                        }}
                    >
                        🎉 Thank You for Registering!
                    </Typography>
                    
                    <Box textAlign="center" mb={4}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            Your registration has been successfully completed.
                        </Typography>
                        <Typography variant="body1">
                            <strong>QR Code ID:</strong> {participantId}
                        </Typography>
                    </Box>

                 {abstractHomeSetup.c_bank_status === 1 && bankDetails && (
    <Card 
        variant="outlined" 
        sx={{ 
            mt: 4,
            borderColor: primaryColor,
            borderWidth: 2,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}
    >
        <CardContent>
            <Box 
                display="flex" 
                alignItems="center" 
                mb={2}
                sx={{ color: primaryColor }}
            >
                <AccountBalanceIcon fontSize="large" sx={{ mr: 2 }} />
                <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                    Payment Instructions
                </Typography>
            </Box>
            
            <Divider sx={{ my: 2, borderColor: primaryColor }} />
            
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Box sx={{ 
                        p: 2, 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 1,
                        '&:hover': {
                            backgroundColor: '#f9f9f9'
                        }
                    }}>
                        <Box display="flex" alignItems="center" mb={1}>
                            <AttachMoneyIcon sx={{ mr: 1, color: primaryColor }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                Bank Name
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="body1" sx={{ 
                                flexGrow: 1,
                                wordBreak: 'break-word',
                                pr: 1
                            }}>
                                {bankDetails.bankName}
                            </Typography>
                            <Tooltip title="Copy to clipboard">
                                <IconButton 
                                    size="small" 
                                    onClick={() => copyToClipboard(bankDetails.bankName)}
                                    sx={{
                                        color: primaryColor,
                                        '&:hover': {
                                            backgroundColor: 'rgba(5, 89, 125, 0.1)'
                                        }
                                    }}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <Box sx={{ 
                        p: 2, 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 1,
                        '&:hover': {
                            backgroundColor: '#f9f9f9'
                        }
                    }}>
                        <Box display="flex" alignItems="center" mb={1}>
                            <PaymentIcon sx={{ mr: 1, color: primaryColor }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                Account Name
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="body1" sx={{ 
                                flexGrow: 1,
                                wordBreak: 'break-word',
                                pr: 1
                            }}>
                                {bankDetails.accountName}
                            </Typography>
                            <Tooltip title="Copy to clipboard">
                                <IconButton 
                                    size="small" 
                                    onClick={() => copyToClipboard(bankDetails.accountName)}
                                    sx={{
                                        color: primaryColor,
                                        '&:hover': {
                                            backgroundColor: 'rgba(5, 89, 125, 0.1)'
                                        }
                                    }}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <Box sx={{ 
                        p: 2, 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 1,
                        '&:hover': {
                            backgroundColor: '#f9f9f9'
                        }
                    }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Account Number
                        </Typography>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="body1" sx={{ 
                                flexGrow: 1,
                                wordBreak: 'break-word',
                                pr: 1
                            }}>
                                {bankDetails.accountNumber}
                            </Typography>
                            <Tooltip title="Copy to clipboard">
                                <IconButton 
                                    size="small" 
                                    onClick={() => copyToClipboard(bankDetails.accountNumber)}
                                    sx={{
                                        color: primaryColor,
                                        '&:hover': {
                                            backgroundColor: 'rgba(5, 89, 125, 0.1)'
                                        }
                                    }}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </Grid>
                
                {bankDetails.iban && (
                    <Grid item xs={12} md={6}>
                        <Box sx={{ 
                            p: 2, 
                            border: '1px solid #e0e0e0', 
                            borderRadius: 1,
                            '&:hover': {
                                backgroundColor: '#f9f9f9'
                            }
                        }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                IBAN
                            </Typography>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography variant="body1" sx={{ 
                                    flexGrow: 1,
                                    wordBreak: 'break-word',
                                    pr: 1
                                }}>
                                    {bankDetails.iban}
                                </Typography>
                                <Tooltip title="Copy to clipboard">
                                    <IconButton 
                                        size="small" 
                                        onClick={() => copyToClipboard(bankDetails.iban)}
                                        sx={{
                                            color: primaryColor,
                                            '&:hover': {
                                                backgroundColor: 'rgba(5, 89, 125, 0.1)'
                                            }
                                        }}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Grid>
                )}
                
                {bankDetails.swiftCode && (
                    <Grid item xs={12} md={6}>
                        <Box sx={{ 
                            p: 2, 
                            border: '1px solid #e0e0e0', 
                            borderRadius: 1,
                            '&:hover': {
                                backgroundColor: '#f9f9f9'
                            }
                        }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                SWIFT Code
                            </Typography>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography variant="body1" sx={{ 
                                    flexGrow: 1,
                                    wordBreak: 'break-word',
                                    pr: 1
                                }}>
                                    {bankDetails.swiftCode}
                                </Typography>
                                <Tooltip title="Copy to clipboard">
                                    <IconButton 
                                        size="small" 
                                        onClick={() => copyToClipboard(bankDetails.swiftCode)}
                                        sx={{
                                            color: primaryColor,
                                            '&:hover': {
                                                backgroundColor: 'rgba(5, 89, 125, 0.1)'
                                            }
                                        }}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Grid>
                )}
            </Grid>
            
            {bankDetails.additionalInfo && (
                <>
                    <Divider sx={{ my: 3, borderColor: primaryColor }} />
                    <Typography variant="body2" sx={{ 
                        whiteSpace: 'pre-line', 
                        fontStyle: 'italic',
                        p: 2,
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1
                    }}>
                        {bankDetails.additionalInfo}
                    </Typography>
                </>
            )}
        </CardContent>
    </Card>
)}

                    {badgeUrl && (
                        <Box textAlign="center" mt={4}>
                            <Button
                                variant="contained"
                                href={badgeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    color: '#fff',
                                    backgroundColor: primaryColor,
                                    fontWeight: 'bold',
                                    '&:hover': {
                                        backgroundColor: primaryColor,
                                        opacity: 0.9,
                                    },
                                }}
                            >
                                View Your Conference Badge
                            </Button>
                        </Box>
                    )}

                    <Box mt={4} textAlign="center">
                        <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic' }}>
                            📧 A confirmation email has been sent to your registered email address.
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            If you don't see it in your inbox, please check your spam or junk folder.
                        </Typography>
                    </Box>
                </Paper>
            </Container>

            {/* Footer */}
            <Grid item xs={12} sm={10} md={8} lg={6} sx={{ mt: 'auto' }}>
                <NewVersionAbstractFooter />
            </Grid>
        </Box>
    );
};

export default ThankYouConferenceParticipant;